import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { CountryDTO } from 'src/app/api/models';
import { AppointmentStatus } from 'src/app/enums/appointment-status-enum';
import { DAY_START_HOUR, HOUR_COUNT } from 'src/app/enums/calendar.constants';
import { EVENT_TYPE_OPTIONS } from 'src/app/enums/constants';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-agenda-filter',
  templateUrl: './agenda-filter.component.html',
  styleUrls: ['./agenda-filter.component.scss']
})
export class AgendaFilterComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  constructor(
    public sharedService: SharedService,
    public translateService: TranslateService
  ) { }

  @Input() resettedFilters: Subject<boolean> = new Subject();
  @Input() salesRooms: Set<string> = new Set();
  @Input() countries: CountryDTO[] = [];

  @ViewChild('formRef') formRef!: ElementRef;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkIfShowMoreAndLessButtonsShouldBeVisible();
  }

  placeholderSearch = 'PAGES.FILTER.SEARCH_FULL_TEXT';

  appointmentStatus = [
    AppointmentStatus.CREATED,
    AppointmentStatus.OPEN,
    AppointmentStatus.DONE,
    AppointmentStatus.CLOSED,
    AppointmentStatus.END
  ];

  @Output() applyFilters = new EventEmitter();
  @Output() resetFilters = new EventEmitter();

  readonly CLIENT_ADVISOR = "clientadvisor";
  readonly ASSISTANT = "assistant";
  readonly RUNNER = "runner";
  readonly SLOT_START = "slotStart";
  readonly SLOT_END = "slotEnd";
  readonly SALES_ROOM = "salesroom";
  readonly COUNTRIES = "countries";
  readonly STATUSES = "statuses";
  readonly ALL_SLOTS = -1;
  readonly EVENT_TYPE_OPTIONS = EVENT_TYPE_OPTIONS;

  searchFormControl = new FormControl('');
  appointmentFormControl = new FormControl('');
  countryFormControl = new FormControl('');
  hasCheckInDateFormControl = new FormControl(null);
  salesRoomsControl = new FormControl<any>([]);
  countriesFormControl = new FormControl<any>([]);
  statusesFormControl = new FormControl<any>([]);
  runnerFormControl = new FormControl('');
  eventTypeFormControl = new FormControl('');
  slotStartControl = new FormControl(this.ALL_SLOTS);
  slotEndControl = new FormControl(this.ALL_SLOTS);

  selectedClientAdvisorId = "";
  selectedAssistantId = "";
  selectedRunnerId = "";


  showMoreFilters: boolean = false;
  showMoreAndLessButtons: boolean = true;
  parsedSalesRooms: any[] = [];

  hoursOptions: any[] = [];
  filtersCounter: number = 0;

  ngOnInit(): void {
    this.builHoursOptions();
    this.slotStartControl.valueChanges.subscribe((value) => {
      let end = this.slotEndControl.value;
      if (typeof value === "number" && typeof end === "number") {
        if (value !== this.ALL_SLOTS && end !== this.ALL_SLOTS) {
          if (value >= end) {
            this.slotEndControl.setValue(this.ALL_SLOTS)
          }
        }
      }
    })
    this.slotEndControl.valueChanges.subscribe((value) => {
      let start = this.slotStartControl.value;
      if (typeof value === "number" && typeof start === "number") {
        if (value !== this.ALL_SLOTS && start !== this.ALL_SLOTS) {
          if (value <= start) {
            this.slotStartControl.setValue(this.ALL_SLOTS)
          }
        }
      }
    })
    this.resettedFilters.subscribe(value => {
      this.reset(true);
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    let salesRooms = changes['salesRooms'];
    if (salesRooms) {
      this.parseSalesRoomsFromSetToArray();
    }
  }

  ngAfterViewInit(): void {
    this.checkIfShowMoreAndLessButtonsShouldBeVisible();
    // document.addEventListener('keypress', this.handleEnterKeyPressed)
  }

  ngOnDestroy(): void {
    if (this.resetFilters) {
      this.resetFilters.unsubscribe();
    }
    // document.removeEventListener('keypress', this.handleEnterKeyPressed)
  }

  handleEnterKeyPressed = ((event: any) => {
    this.apply();
  }).bind(this)

  parseSalesRoomsFromSetToArray() {
    this.parsedSalesRooms = [];
    if (this.salesRooms) {
      (Array.from(this.salesRooms)).forEach(_sr => {
        this.parsedSalesRooms.push(JSON.parse(_sr));
      });
    }
  }

  getSearchParams(): any {
    const appointmentStatus = this.appointmentFormControl.value || ''
    const searchResult = this.searchFormControl.value?.trim() || ''
    const country = this.countryFormControl.value?.trim() || '';
    const hasCheckInDate = this.hasCheckInDateFormControl.value;
    const salesRooms: any = this.salesRoomsControl.value || [];
    const countries = this.countriesFormControl.value || [];
    const statuses = this.statusesFormControl.value || [];
    const eventType = String(this.eventTypeFormControl.value) || '';
    const slotStart = this.slotStartControl.value;
    const slotEnd = this.slotEndControl.value;
    const selectedClientAdvisorId = this.selectedClientAdvisorId;
    const selectedAssistantId = this.selectedAssistantId;
    const selectedRunnerId = this.selectedRunnerId;

    let params: any = {};

    if (!!appointmentStatus) {
      params.Status = appointmentStatus;
    }
    if (!!country) {
      params.Country = country;
    }

    if (!!eventType && parseInt(eventType) >= 0) {
      params.EventType = eventType;
    }

    if (slotStart !== null && slotStart > -1) {
      params.SlotStart = this.hoursOptions[slotStart + 1].label;
    }

    if (slotEnd !== null && slotEnd > -1) {
      params.SlotEnd = this.hoursOptions[slotEnd + 1].label;
    }

    if (!!selectedClientAdvisorId) {
      params.GrandVendeurOid = this.selectedClientAdvisorId;
    }
    if (!!selectedAssistantId) {
      params.CAssistantOid = this.selectedAssistantId;
    }
    if (!!selectedRunnerId) {
      params.RunnerOid = this.selectedRunnerId;
    }
    if (Array.isArray(statuses) && statuses.length > 0) {
      params.Status = statuses.map(val => parseInt(val))
    }
    if (Array.isArray(countries) && countries.length > 0) {
      params.Countries = countries.map(val => val)
    }
    if (Array.isArray(salesRooms) && salesRooms.length > 0) {
      params.IdSalesRoom = salesRooms.map((sr: any) => sr.key)
    }

    return {
      search: searchResult,
      params: params,
      hasCheckInDate: hasCheckInDate
    };
  }

  apply() {
    let params = this.getSearchParams();
    this.updateFiltersCounter(params);
    this.applyFilters.emit(params);
  }

  updateFiltersCounter(filters: any) {
    let { search, params, hasCheckInDate } = filters;
    let counter = 0;
    if (search !== "") {
      counter++;
    }
    if (hasCheckInDate !== null) {
      counter++;
    }
    counter += Object.keys(params).length;
    this.filtersCounter = counter;
  }

  reset(avoidEmittingReset?: boolean) {

    this.searchFormControl = new FormControl('');
    this.hasCheckInDateFormControl = new FormControl(null);
    this.appointmentFormControl = new FormControl('');
    this.countryFormControl = new FormControl('');
    this.salesRoomsControl = new FormControl<any>([]);
    this.countriesFormControl = new FormControl<any>([]);
    this.statusesFormControl = new FormControl<any>([]);
    this.runnerFormControl = new FormControl('');
    this.eventTypeFormControl = new FormControl();
    this.slotStartControl = new FormControl(this.ALL_SLOTS);
    this.slotEndControl = new FormControl(this.ALL_SLOTS);
    this.selectedClientAdvisorId = "";
    this.selectedAssistantId = "";
    this.selectedRunnerId = "";

    this.filtersCounter = 0;
    if (!avoidEmittingReset) {
      this.resetFilters.emit();
    }
  }

  checkIfShowMoreAndLessButtonsShouldBeVisible() {
    let widthSum = 0;
    try {
      let children = Array.prototype.slice.call(this.formRef.nativeElement.children);
      if (children.length === 1) {
        this.showMoreAndLessButtons = false;
        this.showMoreFilters = true;
      } else {
        children.forEach(elem => {
          //Aggiungo 16 per un offset approssimativo relativo al gap di "1em" del form
          widthSum += elem.offsetWidth + 16;
        });
        if (this.formRef.nativeElement.offsetWidth < widthSum && this.showMoreAndLessButtons === false) {
          this.showMoreAndLessButtons = true;
        } else if (this.formRef.nativeElement.offsetWidth > widthSum && this.showMoreAndLessButtons === true) {
          this.showMoreAndLessButtons = false;
        }

      }
    }
    catch (err) {
      console.log(err);
    }
  }

  showFilters(flag: boolean) {
    this.showMoreFilters = flag;
  }

  removeChip(type: string, value: any) {
    let data = null;
    switch (type) {
      case this.SALES_ROOM: {
        data = this.salesRoomsControl.value!;
        this.removeFirst(data, value);
        this.salesRoomsControl.setValue(data);
        break;
      }
      case this.COUNTRIES: {
        data = this.countriesFormControl.value!;
        this.removeFirst(data, value);
        this.countriesFormControl.setValue(data);
        break;
      }
      case this.STATUSES: {
        data = this.statusesFormControl.value!;
        this.removeFirst(data, value);
        this.statusesFormControl.setValue(data);
        break;
      }
    }
  }

  private removeFirst<T>(array: T[], toRemove: T): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }


  optionSelected(event: any, type: any) {
    if ((event && ('key' in event && 'value' in event)) || event === "") {
      let data = event?.key;
      if (event === "") {
        data = "";
      }
      if (data !== undefined) {
        switch (type) {
          case this.CLIENT_ADVISOR: {
            this.selectedClientAdvisorId = data;
            break;
          }
          case this.ASSISTANT: {
            this.selectedAssistantId = data;
            break;
          }
          case this.RUNNER: {
            this.selectedRunnerId = data;
            break;
          }
        }
      }
    }
  }

  getValueSelect(type: string): string {
    switch (type) {
      case this.CLIENT_ADVISOR: {
        return this.selectedClientAdvisorId;
      }
      case this.ASSISTANT: {
        return this.selectedAssistantId;
      }
      case this.RUNNER: {
        return this.selectedRunnerId;
      }
      default: return "";
    }
  }

  builHoursOptions() {
    let options: any[] = [
      {
        value: this.ALL_SLOTS,
        label: this.translateService.instant("PAGES.FILTER.PLACEHOLDERS.ALL_SLOTS")
      }
    ];
    for (let i = 0; i < HOUR_COUNT; i++) {
      let label = String(DAY_START_HOUR + i).padStart(2, '0') + ":00";
      let value = i;
      let option = {
        value,
        label
      }
      options.push(option)
    }
    this.hoursOptions = options;
  }

}
